import React, { useState, ChangeEvent, FormEvent } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import CelebrationScreen from '../../components/containers/CelebrationScreen'
import { AuthPasswordInput, AuthShell, passwordStrength } from './AuthShell'

const ResetPasswordPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  useParams<{ token: string }>()

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  })
  const [validationError, setValidationError] = useState('')
  const [showCelebration, setShowCelebration] = useState(false)

  const { rules, score } = passwordStrength(formData.password)

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()

    if (formData.password !== formData.confirmPassword) {
      setValidationError(t('auth.passwordMismatch'))
      return
    }

    try {
      setShowCelebration(true)
      setTimeout(() => navigate('/login'), 3000)
    } catch {
      setValidationError(t('auth.passwordResetFailed'))
    }
  }

  if (showCelebration) {
    return <CelebrationScreen message={t('auth.passwordResetSuccess')} />
  }

  return (
    <AuthShell
      foot={
        <button
          type="button"
          className="incyia-auth-link"
          onClick={() => navigate('/login')}
        >
          {t('auth.backToSignIn')}
        </button>
      }
    >
      <button
        type="button"
        className="incyia-auth-link incyia-auth-back"
        onClick={() => navigate('/login')}
      >
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          aria-hidden
        >
          <path d="M10 3.5L5.5 8 10 12.5" strokeLinecap="round" />
        </svg>
        {t('auth.backToSignIn')}
      </button>

      <h1 className="incyia-auth-title">{t('auth.chooseNewPassword')}</h1>
      <p className="incyia-auth-sub">{t('auth.chooseNewPasswordSub')}</p>

      {validationError ? (
        <div className="incyia-auth-error">{validationError}</div>
      ) : null}

      <form onSubmit={handleSubmit}>
        <div className="incyia-auth-field">
          <label className="incyia-auth-label" htmlFor="reset-password">
            {t('auth.newPassword')}
            <span className="incyia-auth-req">*</span>
          </label>
          <AuthPasswordInput
            id="reset-password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder={t('auth.passwordPlaceholder')}
            autoComplete="new-password"
            required
          />
          <div
            className={`incyia-auth-pw-meter${score ? ` s${score}` : ''}`}
            aria-hidden
          >
            <span />
            <span />
            <span />
            <span />
          </div>
          <div className="incyia-auth-pw-rules">
            <span className={rules.len ? 'is-ok' : ''}>
              {t('auth.pwRuleLen')}
            </span>
            <span className={rules.up ? 'is-ok' : ''}>
              {t('auth.pwRuleCase')}
            </span>
            <span className={rules.num ? 'is-ok' : ''}>
              {t('auth.pwRuleNum')}
            </span>
            <span className={rules.sym ? 'is-ok' : ''}>
              {t('auth.pwRuleSym')}
            </span>
          </div>
        </div>

        <div className="incyia-auth-field">
          <label className="incyia-auth-label" htmlFor="reset-confirm">
            {t('auth.confirmNewPassword')}
            <span className="incyia-auth-req">*</span>
          </label>
          <input
            id="reset-confirm"
            className="incyia-auth-inp"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder={t('auth.confirmPasswordPlaceholder')}
            autoComplete="new-password"
            required
          />
        </div>

        <button type="submit" className="incyia-auth-submit">
          {t('auth.reset')}
        </button>
      </form>
    </AuthShell>
  )
}

export default ResetPasswordPage
